import type {
  ConfigurationFunctionValueInOut,
  FunctionDescriptorOutput,
} from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, type Optional } from '@festina-life/core'

import { emptyMetaModel } from '../context/metaModelHelpers'
import {
  isModelMetaType,
  type MetaModel,
  type MetaType,
  type ModelMetaType,
} from '../types/metaModel'
import {
  type ConfigurationFieldExpressionValue,
  ConfigurationFieldType,
  type ConfigurationSimpleValueFieldValue,
  type DeclaredConfigurationTemplateFunctionValue,
  type DynamicConfigurationTemplateFunctionValue,
  type EnumeratedValueFrameFieldValue,
  FrameType,
  isNotRelevantField,
  type RangedValueFrameFieldValue,
  type SimpleValueSetupFieldValue,
  type SubTemplateFieldValue,
  type SubTemplateValueSetupFieldValue,
  type TemplateField,
  type TemplateReference,
  type TemplateReferenceValueSetupFieldValue,
  type ValueSetupFieldValue,
  ValueSetupRole,
} from '../types/templateModel'
import { enrichConfigurationField, toIndexedPath } from './helpers'
import {
  functionPath,
  generateDeclaredConfigurationTemplateFunctionValue,
  generateFields,
} from './transformers/toVisualModelConverter'

export const generateEmptyFieldValue = <T extends TemplateField>({
  field,
  meta,
}: {
  field: T
  meta?: MetaModel
}): NonNullable<T['value']> => {
  switch (field.fieldType) {
    case ConfigurationFieldType.SimpleValueSetup:
      return generateEmptySimpleValueSetup(field)
    case ConfigurationFieldType.SubTemplateValueSetup:
      assertNotEmpty(meta)

      return generateEmptySubTemplateValueSetup({
        field,
        meta,
      })
    case ConfigurationFieldType.TemplateValueSetup:
      return generateEmptyTemplateValueSetup(field)
    case ConfigurationFieldType.ConfigurationExpressionValue:
      return generateEmptyConfigurationSimpleValue(field)
  }
}

export const generateEmptyDeclaredFunctionValue = (
  field: DeclaredConfigurationTemplateFunctionValue,
  descriptor: Optional<FunctionDescriptorOutput>,
): DeclaredConfigurationTemplateFunctionValue => ({
  ...field,
  descriptor,
  function: {
    fplusSource: field.function?.fplusSource ?? descriptor?.signature ?? '',
  },
})

export const generateEmptyDynamicFunctionValue = (
  field: DynamicConfigurationTemplateFunctionValue,
): DynamicConfigurationTemplateFunctionValue => ({
  ...field,
  function: {
    fplusSource: field.function?.fplusSource ?? '',
  },
})

const generateEmptySimpleValueSetup = (
  field: TemplateField,
): SimpleValueSetupFieldValue => ({
  ...generateEmptyCommonValueSetup(field),
  role: ValueSetupRole.SIMPLE,
})

export const generateEmptyRangedFrame = (
  fieldValue: ValueSetupFieldValue,
): RangedValueFrameFieldValue => ({
  type: FrameType.RangedValueFrame,
  range: generateEmptyAttributeExpressionValue({
    path: `${fieldValue.path}.frame`,
    fieldName: 'range',
  }),
})
export const generateEmptyEnumeratedFrame = (
  fieldValue: ValueSetupFieldValue,
): EnumeratedValueFrameFieldValue => ({
  type: FrameType.EnumeratedValueFrame,
  entries: generateEmptyAttributeExpressionValue({
    path: `${fieldValue.path}.frame`,
    fieldName: 'entries',
  }),
})

const generateEmptyCommonTemplateField = (field: TemplateField) => ({
  fieldType: field.fieldType,
})

export const generateEmptyCommonValueSetup = (
  field: TemplateField,
): ValueSetupFieldValue => ({
  ...generateEmptyCommonTemplateField(field),
  path: field.path,
  valueConfig: {
    value: 'fixed',
    origin: `${field.path}.valueConfig`,
  },
})

export const generateEmptySubTemplateValueSetup = ({
  field,
  meta,
}: {
  field: TemplateField
  meta: MetaModel
}): SubTemplateValueSetupFieldValue => {
  const modelPropertyType = field.originalPropertyType
  const availableVariantTypes = isModelMetaType(modelPropertyType)
    ? [...modelPropertyType.subTypes, modelPropertyType]
    : []
  const modelMetaTypeVariantTypes =
    availableVariantTypes.filter(isModelMetaType)
  const commonSetup = generateEmptyCommonValueSetup(field)

  return {
    ...commonSetup,
    variants:
      field.role === ValueSetupRole.SINGLE_CLASS_SUB_TEMPLATE &&
      isModelMetaType(availableVariantTypes[0])
        ? [
            newEmptyVariantFromVariantType({
              path: `${field.path}{${availableVariantTypes[0].typeName}}`,
              selectedType: availableVariantTypes[0],
              meta,
              isParentNotRelevant: isNotRelevantField(field),
              updateField: (nextValue) =>
                (
                  field.update as (
                    v: SubTemplateValueSetupFieldValue,
                  ) => Promise<void>
                )({
                  ...commonSetup,
                  availableVariantTypes: modelMetaTypeVariantTypes,
                  variants: [nextValue],
                }),
            }),
          ]
        : [],
    availableVariantTypes: modelMetaTypeVariantTypes,
  }
}

export const generateEmptyTemplateValueSetup = (
  field: TemplateField,
): TemplateReferenceValueSetupFieldValue => ({
  ...generateEmptyCommonValueSetup(field),
  variants: [],
})

const generateEmptyConfigurationSimpleValue = (
  field: TemplateField,
): ConfigurationSimpleValueFieldValue => ({
  ...generateEmptyCommonValueSetup(field),
})

export const generateEmptyTemplateVariant = ({
  key,
  source,
  fieldValue,
}: {
  key?: string
  source?: string
  fieldValue: TemplateReferenceValueSetupFieldValue
}): TemplateReference => {
  const fieldPath = toIndexedPath({
    basePath: fieldValue.path,
    propName: 'variants',
    index: fieldValue.variants.length,
  })

  return {
    source,
    key,
    path: fieldPath,
  }
}

export const newEmptyVariantFromVariantType = ({
  path,
  selectedType,
  meta,
  updateField,
  isParentNotRelevant,
}: {
  path: string
  selectedType: ModelMetaType
  meta: MetaModel
  updateField: (nextValue: SubTemplateFieldValue) => Promise<void>
  isParentNotRelevant: boolean
}): SubTemplateFieldValue => {
  const commonFields = {
    type: selectedType.templateType,
    typeName: selectedType.templateType?.typeName ?? '',
    subTemplateIdentifier: selectedType.typeName,
    modelType: selectedType,
    modelTypeName: selectedType.typeName,
    path,
  }

  const variant: SubTemplateFieldValue = {
    ...commonFields,
    fields: selectedType.templateType
      ? generateFields({
          path,
          meta,
          templateType: selectedType.templateType,
          updateField: (nextFields) =>
            updateField({ ...variant, fields: nextFields }),
          isParentNotRelevant,
        }).filter(
          (t) => t.name !== 'defaultSelected' && t.name !== 'selectable',
        )
      : [],
    declaredFunctions: generateEmptyDeclaredFunctions({
      meta: meta.types.get(selectedType.templateType?.typeName ?? ''),
      updateField: (nextDeclaredFunctions) =>
        updateField({
          ...variant,
          declaredFunctions: nextDeclaredFunctions,
        }),
      path,
      isParentNotRelevant,
    }),
  } satisfies SubTemplateFieldValue

  return variant
}

const generateEmptyDeclaredFunctions = ({
  meta,
  updateField,
  path,
  isParentNotRelevant,
}: {
  path: string
  updateField: (
    nextFunctions: DeclaredConfigurationTemplateFunctionValue[],
  ) => void
  meta: Optional<MetaType>
  isParentNotRelevant: boolean
}) => {
  const declaredFunctionDescriptors =
    (isModelMetaType(meta) &&
      meta.configurationDetails?.functions.filter(
        (functionDescriptor) => !functionDescriptor.internal,
      )) ||
    []

  const allDeclaredFunctionsFromModel = declaredFunctionDescriptors.map(
    (declaredFunctionDescriptor) =>
      generateDeclaredConfigurationTemplateFunctionValue({
        value: undefined,
        path: functionPath({
          basePath: path,
          source: declaredFunctionDescriptor.signature,
        }),
        descriptor: declaredFunctionDescriptor,
        isParentNotRelevant,
      }),
  )

  return allDeclaredFunctionsFromModel.map(
    (declaredFunction) =>
      ({
        ...declaredFunction,
        updateRaw: (nextValue: ConfigurationFunctionValueInOut) => {
          const declaredFunctionPath = functionPath({
            basePath: path,
            source: nextValue.function ?? '',
          })
          const enrichedFunctionValue = enrichConfigurationField({
            value: nextValue,
            metaModel: emptyMetaModel,
            path: declaredFunctionPath,
          })
          const visualFunctionValue =
            generateDeclaredConfigurationTemplateFunctionValue({
              descriptor: declaredFunction.descriptor,
              value: enrichedFunctionValue,
              path,
              isParentNotRelevant: false,
            })

          updateField([
            visualFunctionValue as DeclaredConfigurationTemplateFunctionValue,
          ])
        },
        update: (nextFunctionValue) => {
          updateField(
            nextFunctionValue
              ? [
                  {
                    ...nextFunctionValue,
                    origin: nextFunctionValue.path,
                  },
                ]
              : [],
          )
        },
      }) satisfies DeclaredConfigurationTemplateFunctionValue,
  )
}

export const generateEmptyAttributeExpressionValue = ({
  path,
  fieldName,
}: {
  path: string
  fieldName: string
}): ConfigurationFieldExpressionValue => {
  const fieldPath = `${path}.${fieldName}`

  return {
    path: fieldPath,
    fieldType: ConfigurationFieldType.ConfigurationExpressionValue,
    expression: {
      fplusSource: null,
    },
    origin: fieldPath,
  }
}
